// src/pages/StageForm.js
import { faPlus, faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import { deleteAmenity } from '../services/AmenityServices';
import { deleteBlock } from '../services/BlockServices';
import { deleteComplementaryUnit } from '../services/ComplementaryUnitService';
import { deletePriceList } from '../services/PriceListServices';
import { deleteTypology } from '../services/TypologyServices';
import { getHousingTypologies } from '../services/HousingTypologyServices';
import { getStageById, saveStage } from '../services/StageServices';
import { getProjectById } from '../services/ProjectServices';
import { deleteAdditionalType } from '../services/AdditionalTypeServices';
import { MediaContext } from '../context/MediaContext';
import MediaItems from '../components/MediaItems';

import './Forms.css';
import styles from './Table.module.css';
import { getRotatorsByProyectId } from '../services/RotatorServices';
import { getUnitById, updateUnit } from '../services/UnitServices';

const statusTexts = {
  available: 'Disponible',
  sold: 'Vendida',
  reserved: 'Reservada',
  assigned: 'Asignada',
};

function StageForm() {
  const { id, projectId } = useParams();
  const navigate = useNavigate();

  const [additionalTypes, setAdditionalTypes] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [complementaryUnits, setComplementaryUnits] = useState([]);
  const [deleteInfo, setDeleteInfo] = useState({ type: null, id: null });
  const [error, setError] = useState(null);
  const [housingTypologies, setHousingTypologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaSaveMessage, setMediaSaveMessage] = useState("");
  const { mediaItems, setMediaItems } = useContext(MediaContext);
  const [priceLists, setPriceLists] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [projectSlug, setProjectSlug] = useState('');
  const [rotators, setRotators] = useState([]);
  const [section, setSection] = useState('details');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [stage, setStage] = useState(
    {
      name: '', description: '', stageNumber: '', closingDate: '', slug: '',
      housingTypology: '', orbitViews: '', discount: '', downPayment: '', separation: '', projectedIncrease: '',
      targetFrame: '', glbCode: '', mediaItems: [], createdAtStage: ""
    });
  const [typologies, setTypologies] = useState([]);
  const [updateCounter, setUpdateCounter] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Obtener el nombre del proyecto
        const project = await getProjectById(projectId);
        setProjectName(project.name);
        setProjectSlug(project.slug);
        const lista = await getHousingTypologies();
        setHousingTypologies(lista);
        setComplementaryUnits(project.complementaryUnits || []);

        // Si estamos editando una etapa, obtener sus datos
        if (id) {
          const stageData = await getStageById(id);
          setStage(stageData);
          setBlocks(stageData.blocks);
          setAmenities(stageData.amenities);
          setPriceLists(stageData.priceLists);
          setTypologies(stageData.typologies);
          setAdditionalTypes(stageData.additionalTypes);
          setMediaItems(stageData.mediaItems);
          setComplementaryUnits(stageData.complementaryUnits || []);
          const rotatorList = await getRotatorsByProyectId(projectId);
          setRotators(rotatorList);
        } else {
          setMediaItems([]);
          setComplementaryUnits([]);
          const today = new Date().toISOString().split("T")[0];
          setStage(prev => ({ ...prev, createdAtStage: today }));
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, projectId, setMediaItems, updateCounter]);

  const openDeleteModal = (type, id) => {
    setDeleteInfo({ type, id });
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setSection('details');
    setError(null);
    setLoading(false);
    setShowDeleteModal(false);
    setDeleteInfo({ type: null, id: null, confirmationText: '' });
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteInfo({ type: null, id: null });
  };

  const confirmDelete = async () => {
    if (!deleteInfo.type || !deleteInfo.id) return;

    closeDeleteModal();
    switch (deleteInfo.type) {
      case 'amenity':
        await handleDeleteAmenity(deleteInfo.id);
        break;
      case 'block':
        await handleDeleteBlock(deleteInfo.id);
        break;
      case 'priceList':
        await handleDeletePriceList(deleteInfo.id);
        break;
      case 'typology':
        await handleDeleteTypology(deleteInfo.id);
        break;
      case 'complementaryUnit':
        await handleDeleteComplementaryUnit(deleteInfo.id);
        break;
      case 'additionalType':
        await handleDeleteAdditionalType(deleteInfo.id);
        break;
      default:
        break;
    }
  };

  const handleDeleteAmenity = async (id) => {
    setError(null);
    try {
      await deleteAmenity(id);
      setAmenities((prevAmenities) => prevAmenities.filter((amenity) => amenity.id !== id));
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeleteComplementaryUnit = async (id) => {
    setError(null);
    setLoading(true);
    try {
      // Busca la complementary unit a eliminar
      const compUnit = complementaryUnits.find(unit => unit.id === id);

      await deleteComplementaryUnit(id);

      setComplementaryUnits(prev => prev.filter(unit => unit.id !== id));
      setUpdateCounter(prev => prev + 1);

      // Si está asignada a una unidad principal, actualiza esa unidad
      if (compUnit?.unitId) {
        const mainUnitData = await getUnitById(compUnit.unitId);
        if (mainUnitData && mainUnitData.unit) {
          const updatedMainUnit = {
            ...mainUnitData.unit,
            complementaryUnits: (mainUnitData.unit.complementaryUnits || []).filter(cu => cu.id !== id)
          };
          await updateUnit(compUnit.unitId, updatedMainUnit);
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdditionalType = async (id,force = false, newAdditionalTypeId = null) => {
    setError(null);
    setLoading(true);
    try {
      await deleteAdditionalType(id,force, newAdditionalTypeId);
      setAdditionalTypes(prev =>
        prev.filter(type => type.id !== id)
      );
      setUpdateCounter(prev => prev + 1);
      setSection('details');
    } catch (error) {
      setError(error.message);
      setDeleteInfo({ type: 'additionalType', id, confirmationText: '' });
      setSection('deleteAdditionalType');
      setLoading(false);
    }
  };

  const handleDeleteBlock = async (id, force = false) => {
    setError(null);
    setLoading(true);
    try {
      await deleteBlock(id, force);
      setUpdateCounter(updateCounter + 1);
      setSection('details');
    } catch (error) {
      setError(error.message);
      setDeleteInfo({ type: 'block', id, confirmationText: '' });
      setSection('deleteBlock');
      setLoading(false);
    }
  };

  const handleDeletePriceList = async (id) => {
    setError(null);
    setLoading(true);
    try {
      await deletePriceList(id);
      setUpdateCounter(updateCounter + 1);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleDeleteTypology = async (id, force = false, newTypologyId = null) => {
    setError(null);
    setLoading(true);
    try {
      await deleteTypology(id, force, newTypologyId);
      setUpdateCounter(updateCounter + 1);
      setSection('details');
    } catch (error) {
      setError(error.message);
      setDeleteInfo({ type: 'typology', id, confirmationText: '' });
      setSection('deleteTypology');
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!id) return;

    try {
      stage.id = id;
      stage.mediaItems = mediaItems;
      stage.projectId = projectId;
      await saveStage(stage);
      setMediaSaveMessage("¡MediaItem guardado exitosamente!");
      setTimeout(() => setMediaSaveMessage(""), 3000);
    } catch (error) {
      setError(error.message);
    }
  }

  const handleStageNumberChange = (e) => {
    const newStageNumber = e.target.value;
    const newSlug = `${projectName.toLowerCase().replace(/[^\w\s]/gi, '').replace(/\s+/g, '-')}-${newStageNumber}`;
    setStage({ ...stage, stageNumber: newStageNumber, slug: newSlug });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      stage.mediaItems = mediaItems;
      if (id) {
        stage.id = id;
      }
      stage.projectId = projectId;
      await saveStage(stage);
      navigate(`/projects/edit/${projectId}`);
    } catch (error) {
      setError(error.message);
    }
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  if (section === 'deleteAdditionalType') {
    return (
      <div className="delete-warning-container">
      <h2 style={{ color: 'red' }}>Advertencia</h2>
      <p>
        Si confirmas la eliminación de la tipología de adicionales, las unidades complementarias relacionadas serán asignadas a una nueva tipología.
        Esta operación no puede ser reversada.
      </p>
      <p>Por favor, selecciona una nueva tipología para reasignar las unidades complementarias relacionadas:</p>
      <select
        onChange={(e) => setDeleteInfo({ ...deleteInfo, newAdditionalTypeId: e.target.value })}
        value={deleteInfo.newAdditionalTypeId || ""}
      >
        <option value="">Selecciona una tipología de adicionales</option>
        {additionalTypes
        .filter((additionalType) => additionalType.id !== deleteInfo.id)
        .map((additionalType) => (
          <option key={additionalType.id} value={additionalType.id}>
          {additionalType.name}
          </option>
        ))}
      </select>
      <div className="actions-container">
        <button
        className="btn-submit"
        disabled={!deleteInfo.newAdditionalTypeId}
        onClick={() => handleDeleteAdditionalType(deleteInfo.id, true, deleteInfo.newAdditionalTypeId)}
        >
        Confirmar Eliminación
        </button>
        <button className="btn-cancel" onClick={cancelDelete}>
        Cancelar
        </button>
      </div>
      </div>
    );
  }
  if (section === 'deleteBlock') {
    return (
      <div className="delete-warning-container">
        <h2 style={{ color: 'red' }}>Advertencia</h2>
        <p>
          Si confirmas la eliminación del bloque, se eliminarán también las unidades y amenidades relacionadas.
          Esta operación no puede ser reversada.
        </p>
        <p>Por favor, escribe "eliminar bloque" para confirmar:</p>
        <input
          type="text"
          placeholder="Escribe 'eliminar bloque' aquí"
          onChange={(e) => setDeleteInfo({ ...deleteInfo, confirmationText: e.target.value })}
        />
        <div className="actions-container">
          <button
            className="btn-submit"
            disabled={deleteInfo.confirmationText !== "eliminar bloque"}
            onClick={() => handleDeleteBlock(deleteInfo.id, true)}
          >
            Confirmar Eliminación
          </button>
          <button className="btn-cancel" onClick={cancelDelete}>
            Cancelar
          </button>
        </div>
      </div>
    );
  }
  if (section === 'deleteTypology') {
    return (
      <div className="delete-warning-container">
      <h2 style={{ color: 'red' }}>Advertencia</h2>
      <p>
        Si confirmas la eliminación de la tipología, las unidades relacionadas serán asignadas a una nueva tipología.
        Esta operación no puede ser reversada.
      </p>
      <p>Por favor, selecciona una nueva tipología para reasignar las unidades relacionadas:</p>
      <select
        onChange={(e) => setDeleteInfo({ ...deleteInfo, newTypologyId: e.target.value })}
        value={deleteInfo.newTypologyId || ""}
      >
        <option value="">Selecciona una tipología</option>
        {typologies
        .filter((typology) => typology.id !== deleteInfo.id)
        .map((typology) => (
          <option key={typology.id} value={typology.id}>
          {typology.name}
          </option>
        ))}
      </select>
      <div className="actions-container">
        <button
        className="btn-submit"
        disabled={!deleteInfo.newTypologyId}
        onClick={() => handleDeleteTypology(deleteInfo.id, true, deleteInfo.newTypologyId)}
        >
        Confirmar Eliminación
        </button>
        <button className="btn-cancel" onClick={cancelDelete}>
        Cancelar
        </button>
      </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <h2>{id ? 'Editar Etapa' : 'Nueva Etapa'}</h2>
        {error && (
          <div>
            <div className='error-message'>
              Hubo un error: {error}
            </div>
            <br />
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="stage-createdAtStage">Fecha de creación</label>
            <input
              type="date"
              id="stage-createdAtStage"
              value={stage.createdAtStage || ""}
              disabled
              readOnly
            />
          </div>

          <div className="form-group">
            <label htmlFor="stage-name">Nombre de la Etapa</label>
            <input
              type="text"
              id="stage-name"
              value={stage.name}
              onChange={(e) => setStage({ ...stage, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="stage-description">Descripción</label>
            <textarea
              id="stage-description"
              value={stage.description}
              onChange={(e) => setStage({ ...stage, description: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="stage-stageNumber">Número de Etapa</label>
            <input
              type="number"
              id="stage-stageNumber"
              value={stage.stageNumber}
              onChange={(e) => {
                const value = e.target.value.slice(0, 5);
                handleStageNumberChange({ target: { value } });
              }}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="stage-closingDate">Fecha de Entrega</label>
            <input
              type="date"
              id="stage-closingDate"
              value={stage.closingDate}
              onChange={(e) => setStage({ ...stage, closingDate: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="stage-housingTypology">Tipo de proyecto</label>
            <select
              id="stage-housingTypology"
              value={stage.housingTypology}
              onChange={(e) => setStage({ ...stage, housingTypology: e.target.value })}
            >
              <option value="">Selecciona una tipología de vivienda</option>
              {housingTypologies.map((housingTypology) => (
                <option key={housingTypology.id} value={housingTypology.code}>
                  {housingTypology.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="stage-orbitViews">Orbit Views</label>
            <select
              id="stage-orbitViews"
              value={stage.orbitViews}
              onChange={(e) => setStage({ ...stage, orbitViews: e.target.value })}>
              <option value="">Seleccione un orbit</option>
              {rotators.map((orbitViews) => (
                <option key={orbitViews.id} value={orbitViews.name}>
                  {orbitViews.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="stage-discount">Porcentaje de Descuento</label>
            <input
              type="number"
              id="stage-discount"
              value={stage.discount}
              onChange={(e) => setStage({ ...stage, discount: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="stage-downPayment">Porcentaje de cuota inicial</label>
            <input
              type="number"
              id="stage-downPayment"
              value={stage.downPayment}
              onChange={(e) => setStage({ ...stage, downPayment: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="stage-separation">Porcentaje de separación</label>
            <input
              type="number"
              id="stage-separation"
              value={stage.separation}
              onChange={(e) => setStage({ ...stage, separation: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="stage-separation">Incremento esperado a la entrega (%)</label>
            <input
              type="number"
              id="stage-projectedIncrease"
              value={stage.projectedIncrease}
              onChange={(e) => setStage({ ...stage, projectedIncrease: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="stage-targetFrame">Frame en rotación 360°</label>
            <input
              type="number"
              id="stage-targetFrame"
              value={stage.targetFrame}
              onChange={(e) => setStage({ ...stage, targetFrame: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="stage-glb">Código GLB</label>
            <input
              type="text"
              id="stage-glb"
              value={stage.glbCode}
              onChange={(e) => setStage({ ...stage, glbCode: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="stage-slug">Slug (auto-generado)</label>
            <input
              id="stage-slug"
              type="text"
              value={stage.slug}
              readOnly
            />
          </div>

          <div className="form-group">
            <button type="submit" className="btn-submit">{id ? 'Guardar Cambios' : 'Agregar Etapa'}</button>
          </div>
        </form>
        <div className="form-footer">
          <Link to={`/projects/edit/${projectId}`} className="back-link">Regresar al Proyecto</Link>
        </div>
      </div>

      {id && (
        <div className="stages-container">
          <div className="sections-container">
            <MediaItems projectSlug={projectSlug} objectType='stage' objectSlug={stage.slug} />
            <button className="btn-submit" onClick={() => handleSave()}>Grabar</button>
            {mediaSaveMessage && <div className="media-save-message">{mediaSaveMessage}</div>}
          </div>

          <div className="sections-container">
            <h2>Listas de precios</h2>
            <button className="btn-add" onClick={() => navigate('/priceList/new/' + id)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '60%' }}>Código</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {priceLists.map((list) => (
                  <tr key={list.id}>
                    <td>{list.code}</td>
                    <td>
                      <div className="actions-container">
                        <button className="btn-edit" onClick={() => navigate(`/priceList/edit/${id}/${list.id}`)}>
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button className="btn-delete" onClick={() => openDeleteModal("priceList", list.id)}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sections-container">
            <h2>Bloques</h2>
            <button className="btn-add" onClick={() => navigate('/blocks/new/' + id)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '60%' }}>Nombre</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {blocks.map((block) => (
                  <tr key={block.id}>
                    <td>{block.name}</td>
                    <td>
                      <div className="actions-container">
                        <button className="btn-edit" onClick={() => navigate(`/blocks/edit/${id}/${block.id}`)}>
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button className="btn-delete" onClick={() => openDeleteModal("block", block.id)}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sections-container">
            <h2>Tipologías</h2>
            <button className="btn-add" onClick={() => navigate('/typologies/new/' + id)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '10%' }}>Código</th>
                  <th style={{ width: '60%' }}>Nombre</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {typologies.map((typology) => (
                  <tr key={typology.id}>
                    <td>{typology.code}</td>
                    <td>{typology.name}</td>
                    <td>
                      <div className="actions-container">
                        <button className="btn-edit" onClick={() => navigate(`/typologies/edit/${id}/${typology.id}`)}>
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button className="btn-delete" onClick={() => openDeleteModal("typology", typology.id)}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sections-container">
            <h2>Tipos de adicionales</h2>
            <button className="btn-add" onClick={() => navigate('/additionalTypes/new/' + id)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '10%' }}>Código</th>
                  <th style={{ width: '60%' }}>Nombre</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {additionalTypes.map((additionalType) => (
                  <tr key={additionalType.id}>
                    <td>{additionalType.code}</td>
                    <td>{additionalType.name}</td>
                    <td>
                      <div className="actions-container">
                        <button className="btn-edit" onClick={() => navigate(`/additionalTypes/edit/${id}/${additionalType.id}`)}>
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button className="btn-delete" onClick={() => openDeleteModal("additionalType", additionalType.id)}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sections-container">
            <h2>Amenidades</h2>
            <button className="btn-add" onClick={() => navigate(`/amenities/new/stage/${id}`)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '60%' }}>Nombre de la Amenidad</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {amenities.map((amenity) => (
                  <tr key={amenity.id}>
                    <td>{amenity.name}</td>
                    <td>
                      <div className="actions-container">
                        <button className="btn-edit" onClick={() => navigate(`/amenities/edit/stage/${id}/${amenity.id}`)}>
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button className="btn-delete" onClick={() => openDeleteModal("amenity", amenity.id)}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


          <div className="sections-container">
            <h2>Unidades Complementarias</h2>
            <button className="btn-add" onClick={() => navigate(`/complementary-units/new/${id}`)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Tipo</th>
                  <th>Asignada</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {complementaryUnits.map((unit) => {
                  const typeInfo = additionalTypes.find(t => t.id === unit.additionalTypeId);
                  return (
                    <tr key={unit.id}>
                      <td>{unit.code}</td>
                      <td>
                        {typeInfo?.name || 'Sin tipo'}
                        {typeInfo?.group && ` (${typeInfo.group === 'PARK' ? 'Parqueadero' : 'Cuarto útil'})`}
                      </td>
                      <td>{unit.assigned ? 'Sí' : 'No'}</td>
                      <td>
                        {statusTexts[unit.status] || unit.status}
                      </td>
                      <td>
                        <div className="actions-container">
                          <button className="btn-edit" onClick={() => navigate(`/complementary-units/edit/${stage.id}/${unit.id}`)}>
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                          <button className="btn-delete" onClick={() => openDeleteModal("complementaryUnit", unit.id)}>
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {showDeleteModal && (
              <div className="media-modal-overlay" onClick={closeDeleteModal}>
                <div className="media-modal" onClick={e => e.stopPropagation()}>
                  <h3 className="modal-content-h3">¿Seguro que deseas eliminar este archivo?</h3>
                  <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "center" }}>
                    <button className="btn-submit" onClick={confirmDelete}>Aceptar</button>
                    <button className="btn-cancel" onClick={closeDeleteModal}>Cancelar</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StageForm;
